// Metric explanations and insights for Cognition Analysis

export interface MetricExplanation {
  definition: string;
  calculation?: string;
  interpretation?: string;
  goodRange?: string;
}

export interface InsightChip {
  question: string;
  answer: string;
}

export interface CommandItem {
  question: string;
  answer: string;
  category: string;
}

// Metric Definitions for Cognition
export const cognitionMetricExplanations: Record<string, MetricExplanation> = {
  "Product Perception": {
    definition: "How AI models understand and describe individual products in your catalog.",
    calculation: "Scored by analyzing accuracy of product descriptions, feature mentions, sentiment, and competitive positioning across all AI models. Averaged across all product mentions.",
    interpretation: "High scores mean AI accurately represents your products with correct features, benefits, and positioning. Low scores suggest missing information, inaccuracies, or unfavorable descriptions.",
    goodRange: "75+ is strong product perception. 60-74 needs optimization. Below 60 requires immediate content updates.",
  },
  "Attribute Strength": {
    definition: "Measures how strongly AI models associate specific attributes with your brand.",
    calculation: "Tracks mention frequency, emphasis prominence, and contextual relevance of attributes. Scored 0-100 based on how consistently AI models highlight each attribute.",
    interpretation: "Strong attributes are what AI emphasizes when describing you. Weak attributes represent missed positioning opportunities or misalignment with your brand strategy.",
    goodRange: "Strategic attributes should score 75+. Scores below 50 indicate the attribute isn't strongly associated with your brand in AI understanding.",
  },
  "Competitive Attribute Gap": {
    definition: "The difference between your attribute strength and competitors' average across key brand dimensions.",
    calculation: "Your attribute score minus the average competitor attribute score. Positive gaps indicate advantages; negative gaps show weaknesses.",
    interpretation: "Positive gaps (+10 or more) are competitive advantages AI recognizes. Negative gaps are weaknesses where competitors outperform you in AI perception.",
    goodRange: "+15 or more is a strong advantage. -10 to +10 is parity. Below -10 is a significant disadvantage.",
  },
  "Keyword Relevance": {
    definition: "How relevant and important specific keywords are when AI models describe your brand.",
    calculation: "Measured by keyword mention frequency, position in responses, and correlation with positive outcomes. Scored 0-100 for each keyword.",
    interpretation: "High-relevance keywords drive AI perception and recommendations. Understanding which keywords matter helps you optimize content strategy.",
    goodRange: "90+ is critical keyword. 75-89 is strong. 60-74 is moderate. Below 60 is low impact.",
  },
  "Model Perception Variance": {
    definition: "How consistently different AI models perceive your products and attributes.",
    calculation: "Standard deviation of perception scores across all models. Lower variance means more consistent representation.",
    interpretation: "Low variance (consistent) means users get the same brand experience regardless of which AI they use. High variance indicates inconsistent brand representation requiring alignment.",
    goodRange: "Variance below 10 points is excellent. 10-15 is acceptable. Above 15 indicates inconsistent representation.",
  },
  "Sentiment Quality": {
    definition: "The tone and favorability of language AI models use when describing your products or attributes.",
    calculation: "Natural language processing analyzes sentiment across all mentions, categorized as Positive, Neutral, or Negative.",
    interpretation: "Positive sentiment builds trust and preference. Neutral suggests factual but undifferentiated positioning. Negative indicates reputation issues or misinformation.",
    goodRange: "75%+ positive is excellent. 60-74% is competitive. Below 60% needs investigation.",
  },
};

// Insight chips for product analysis
export const productInsights: InsightChip[] = [
  {
    question: "Why analyze products separately?",
    answer: "Individual products may have different perception scores than your overall brand. One strong product can carry weak ones, or vice versa. Product-level analysis reveals which specific products drive overall brand perception.",
  },
  {
    question: "What drives product perception?",
    answer: "Product perception is driven by: accurate feature descriptions (40%), competitive positioning (25%), sentiment/reviews (20%), and availability of rich content like images and specs (15%).",
  },
  {
    question: "How do I improve product scores?",
    answer: "Focus on: 1) Adding detailed product descriptions to your website, 2) Ensuring accurate specs are in schema markup, 3) Publishing product reviews and case studies, 4) Creating comparison content vs. competitors.",
  },
];

// Insight chips for attribute analysis
export const attributeInsights: InsightChip[] = [
  {
    question: "What are brand attributes?",
    answer: "Brand attributes are the characteristics, qualities, and associations that define how your brand is perceived — like 'durable,' 'innovative,' 'affordable,' or 'eco-friendly.' They shape brand positioning.",
  },
  {
    question: "Why do attribute gaps matter?",
    answer: "Gaps show where competitors are beating you in AI perception. If 'value' has a -15 gap, AI sees competitors as better value. These gaps directly influence recommendations and comparisons.",
  },
  {
    question: "How do I strengthen attributes?",
    answer: "Create content that explicitly demonstrates the attribute. For 'innovation': publish R&D stories, patent announcements, feature launches. For 'sustainability': share certifications, impact reports, eco-initiatives.",
  },
];

// Insight chips for keyword analysis
export const keywordInsights: InsightChip[] = [
  {
    question: "How are keywords different from attributes?",
    answer: "Attributes are abstract qualities (durable, premium). Keywords are specific phrases users search for and AI uses to categorize you ('waterproof hiking boots,' 'enterprise CRM software'). Keywords drive discovery.",
  },
  {
    question: "What makes a keyword high-relevance?",
    answer: "High-relevance keywords appear frequently in AI responses about your brand, appear early in descriptions, and correlate with positive recommendations. They're how AI 'categorizes' and 'finds' you.",
  },
  {
    question: "Should I target all high-volume keywords?",
    answer: "No. Focus on high-relevance keywords that align with your positioning. A keyword can have huge volume but low relevance if it doesn't accurately describe your value proposition or target audience.",
  },
];

// Insight chips for model variance
export const modelVarianceInsights: InsightChip[] = [
  {
    question: "Why do models perceive things differently?",
    answer: "Each AI model is trained on different data sources, timeframes, and algorithms. ChatGPT might emphasize recent press, while Claude focuses on technical specs. Understanding variance helps you address gaps.",
  },
  {
    question: "Is variance always bad?",
    answer: "Not always. Some variance is natural. But large gaps (20+ points) suggest one model has outdated or missing information, creating inconsistent user experiences across platforms.",
  },
  {
    question: "How do I reduce variance?",
    answer: "Ensure consistent, authoritative content across all sources AI models access: your website, press releases, product documentation, and third-party reviews. Update all platforms simultaneously.",
  },
];

// Command palette items for Cognition
export const cognitionCommands: CommandItem[] = [
  {
    question: "Why is my product perception score low?",
    answer: "Low product perception scores usually stem from: missing product details on your website, outdated or incorrect specifications, lack of product reviews, or competitors having richer content. Check the 'Product-Level Insights' section to identify which products need attention and what information is missing or inaccurate.",
    category: "Product Analysis",
  },
  {
    question: "How do I improve attribute strength?",
    answer: "To strengthen an attribute, create content that explicitly demonstrates it. For example, if you want to strengthen 'innovation': publish R&D stories, announce patents, showcase new features, and create comparison content highlighting innovative aspects. Repeat the attribute in headings, product descriptions, and About pages.",
    category: "Attribute Analysis",
  },
  {
    question: "What's a competitive attribute gap?",
    answer: "A competitive attribute gap shows the difference between how strongly AI associates an attribute with your brand vs. competitors. Positive gaps (+10 or more) are advantages. Negative gaps show weaknesses where competitors outperform you in AI perception. Focus on closing negative gaps in strategic attributes.",
    category: "Competitive Analysis",
  },
  {
    question: "Which keywords should I prioritize?",
    answer: "Prioritize keywords with high relevance (90+) that align with your brand positioning and target audience. High volume doesn't always mean high value — a keyword is only useful if it accurately represents your value proposition and drives qualified discovery.",
    category: "Keyword Strategy",
  },
  {
    question: "Why do different AI models score me differently?",
    answer: "Models are trained on different data sources and timeframes. ChatGPT might emphasize recent press coverage, while Claude focuses on technical documentation. Large variance (15+ points) indicates inconsistent brand representation across sources. Ensure all platforms have consistent, up-to-date information.",
    category: "Model Analysis",
  },
  {
    question: "How do product scores affect overall brand perception?",
    answer: "Your overall brand score is partially driven by product-level perception. Strong products can lift weak ones, but weak products can drag down your brand. If individual products score 20+ points below your brand score, they're hurting your overall perception and need content optimization.",
    category: "Product Analysis",
  },
  {
    question: "What's the difference between attributes and keywords?",
    answer: "Attributes are abstract brand qualities (innovative, durable, premium). Keywords are specific phrases users search for ('waterproof hiking boots'). Attributes shape positioning and sentiment. Keywords drive discovery and categorization. Both matter, but they serve different roles.",
    category: "Strategy",
  },
  {
    question: "How do I find gaps in my competitive positioning?",
    answer: "Look at the Competitive Attribute Positioning section. Attributes where you score significantly below competitors (gaps of -10 or worse) are weaknesses. Attributes where you score +10 or more are advantages. Focus content strategy on closing critical gaps and amplifying advantages.",
    category: "Competitive Analysis",
  },
  {
    question: "Should I focus on products or attributes first?",
    answer: "If you have 3 or fewer core products, focus on products first — improve descriptions, specs, and content for each. If you have a large catalog (10+ products), focus on attributes to create brand-level positioning, then apply learnings to individual products.",
    category: "Strategy",
  },
  {
    question: "What causes high sentiment variance across models?",
    answer: "High sentiment variance means some models describe you positively while others are neutral or negative. This often happens when: 1) Recent negative press affects newer models, 2) Different models access different review sources, or 3) Inconsistent messaging across your owned channels creates confusion.",
    category: "Sentiment Analysis",
  },
];
